// Shared, user-facing presentation of the retake cooldown.
//
// The server encodes the unlock timestamp in the thrown message as
// `RETAKE_COOLDOWN_ACTIVE:<iso>`. Every UI call site must run its errors
// through `retakeCooldownMessage` so users never see the raw code.

import type { Lang } from "@/lib/bousala/i18n";

export const RETAKE_COOLDOWN_CODE = "RETAKE_COOLDOWN_ACTIVE";

/** Readable date — never an ISO string. Latin numerals in Arabic. */
export function formatUnlockDate(iso: string, lang: Lang): string {
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "ar-u-nu-latn", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(iso));
}

/** Localised sentence for a known (or unknown) unlock date. */
export function cooldownMessage(iso: string | null, lang: Lang): string {
  const days = iso
    ? Math.max(1, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000))
    : null;
  if (lang === "en") {
    return iso
      ? `You can retake the test in ${days} day${days === 1 ? "" : "s"} — on ${formatUnlockDate(iso, lang)}.`
      : "You can't retake the test yet.";
  }
  return iso
    ? `تقدر تعيد الاختبار بعد ${days} يوم — بتاريخ ${formatUnlockDate(iso, lang)}.`
    : "ما بتقدر تعيد الاختبار هلق.";
}

function errorText(err: unknown): string {
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message?: unknown }).message ?? "");
  }
  return "";
}

/**
 * Returns a ready-to-display cooldown message, or null when `err` is not a
 * cooldown error.
 */
export function retakeCooldownMessage(err: unknown, lang: Lang): string | null {
  const raw = errorText(err);
  if (!raw.includes(RETAKE_COOLDOWN_CODE)) return null;
  const match = raw.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?/);
  return cooldownMessage(match ? match[0] : null, lang);
}
